import React, { useState, useMemo } from "react";
import { useResignedEmpDetails, useReactivateEmployee } from "../../hooks/useEmployees";
import { TH, TD, Row, TableWrap, TABLE_STYLE, AVATAR_SX } from "../common/tableStyles";
import { formatDate } from '../../utils/dateFormat';

import CloseIcon from '@mui/icons-material/Close';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import TabIcon from '@mui/icons-material/Tab';
import { BASE_API_URL } from "../../api";
import {
    Box,
    Paper,
    Avatar,
    Tooltip,
    IconButton,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Typography,
    Chip,
    TablePagination,
    CircularProgress
} from "@mui/material";
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';
import './resigned.css';

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: '22px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const SOFT_INPUT_SX = { '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } };

// Soft tinted row action button (reactivate = green).
const REACTIVATE_BTN_SX = { color: '#16a34a', bgcolor: '#f0fdf4', '&:hover': { bgcolor: '#dcfce7' } };

const ResignedEmp = () => {
    const { data: resignedData = [], isLoading, error } = useResignedEmpDetails();
    const { mutate: reactivate, isPending: isReactivating } = useReactivateEmployee();
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [filters, setFilters] = useState({
        search: '',
        role: 'all',
        sortBy: 'newest'
    });

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Unique Roles for Filter
    const roles = useMemo(() => {
        const uniqueRoles = [...new Set(resignedData.map(emp => emp.designation).filter(Boolean))];
        return uniqueRoles.sort();
    }, [resignedData]);

    // Filtered & Sorted Data
    const filteredData = useMemo(() => {
        let result = [...resignedData];

        // Search Filter (Name, ID, Email, Phone)
        if (filters.search) {
            const query = filters.search.toLowerCase();
            result = result.filter(emp =>
                emp.name?.toLowerCase().includes(query) ||
                emp.employee_no?.toLowerCase().includes(query) ||
                emp.email?.toLowerCase().includes(query) ||
                emp.mobile_number?.includes(query)
            );
        }

        // Role Filter
        if (filters.role !== 'all') {
            result = result.filter(emp => emp.designation === filters.role);
        }

        // Robust date → timestamp. Handles ISO ("2025-06-15") AND day-first
        // formats ("15/06/2025", "15-06-2025") that `new Date()` parses to NaN —
        // which was silently breaking the sort and leaving the (older-first)
        // backend order in place. Missing dates fall back to 0 (sorted last).
        const toTime = (v) => {
            if (!v) return 0;
            const d = new Date(v);
            if (!Number.isNaN(d.getTime())) return d.getTime();
            const m = String(v).match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
            if (m) {
                const t = new Date(`${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`).getTime();
                return Number.isNaN(t) ? 0 : t;
            }
            return 0;
        };

        // The resigned-list endpoint returns no ordering and `updated_at` is not
        // serialized. Use the actual resignation date, falling back to the
        // resignation record's created_at (set when resigned via the form). Rows
        // resigned via the quick delete have neither → 0 → sorted last.
        const resignTime = (emp) =>
            toTime(emp.resignation_date || emp.resignation_info?.created_at);

        // Sorting
        result.sort((a, b) => {
            if (filters.sortBy === 'newest') {
                return resignTime(b) - resignTime(a);
            } else if (filters.sortBy === 'oldest') {
                return resignTime(a) - resignTime(b);
            } else if (filters.sortBy === 'name') {
                return (a.name || '').localeCompare(b.name || '');
            }
            return 0;
        });

        return result;

    }, [resignedData, filters]);

    const handleRowClick = (employee) => {
        setSelectedEmployee(employee);
    };

    const handleClose = () => {
        setSelectedEmployee(null);
    };

    const handleReactivate = (employeeNo, e) => {
        if (e) e.stopPropagation();
        if (window.confirm(`Are you sure you want to reactivate this employee?`)) {
            reactivate(employeeNo, {
                onSuccess: () => setSelectedEmployee(null)
            });
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0); // Reset to first page on filter change
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            role: 'all',
            sortBy: 'newest'
        });
        setPage(0);
    };

    // Pagination Handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Header card */}
            <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <PersonOffIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                                Resigned Employees
                            </Typography>
                            <Chip
                                label={`${filteredData.length} Found / ${resignedData.length} Total`}
                                size="small"
                                sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: '#eef4ff', color: ACCENT, border: 'none' }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Inactive and resigned employees across the organization
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Filters card */}
            <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 }, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search by Name, ID, Email, Phone..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: { xs: '100%', sm: 300 }, ...SOFT_INPUT_SX }}
                />
                <FormControl size="small" sx={{ minWidth: 130, ...SOFT_INPUT_SX }}>
                    <InputLabel>Role</InputLabel>
                    <Select
                        value={filters.role}
                        label="Role"
                        onChange={(e) => handleFilterChange('role', e.target.value)}
                        MenuProps={SELECTED_MENU_PROPS}
                    >
                        <MenuItem value="all">All Roles</MenuItem>
                        {roles.map(role => (
                            <MenuItem key={role} value={role}>{role}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150, ...SOFT_INPUT_SX }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                        value={filters.sortBy}
                        label="Sort By"
                        onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                        MenuProps={SELECTED_MENU_PROPS}
                    >
                        <MenuItem value="newest">Newest Resigned</MenuItem>
                        <MenuItem value="oldest">Oldest Resigned</MenuItem>
                        <MenuItem value="name">Name (A-Z)</MenuItem>
                    </Select>
                </FormControl>
                <Box flexGrow={1} />
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={clearFilters}
                    size="small"
                    sx={{ ...OUTLINED_BTN, height: '40px' }}
                >
                    Reset
                </Button>
            </Paper>

            {/* Employees Table */}
            {isLoading ? (
                <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2, py: 8 }}>
                    <CircularProgress sx={{ color: ACCENT }} />
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>Loading resigned employees…</Typography>
                </Paper>
            ) : error ? (
                <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 8 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PersonOffIcon />
                    </Box>
                    <Typography variant="h6" sx={{ color: '#111827', fontWeight: 700 }}>Couldn't load resigned employees</Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>{error?.message || 'Please try again.'}</Typography>
                </Paper>
            ) : filteredData.length > 0 ? (
                <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.5, md: 2 } }}>
                    <TableWrap>
                        <table style={TABLE_STYLE}>
                            <thead>
                                <tr>
                                    <th style={TH}>Emp.No</th>
                                    <th style={TH}>Name</th>
                                    <th style={TH}>Role</th>
                                    <th style={TH}>Date of Joining</th>
                                    <th style={TH}>Resigned Date</th>
                                    <th style={TH}>Reason</th>
                                    <th style={TH}>Report Manager</th>
                                    <th style={TH}>Annual CTC</th>
                                    <th style={TH}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((emp) => (
                                        <Row
                                            key={emp.id}
                                            style={{
                                                backgroundColor: selectedEmployee?.id === emp.id ? '#eef4ff' : undefined,
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleRowClick(emp)}
                                        >
                                            <td style={TD}>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {emp.employee_no}
                                                </Typography>
                                            </td>
                                            <td style={TD}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={AVATAR_SX}>
                                                        {emp.name?.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="body2" fontWeight="600">{emp.name}</Typography>
                                                        <Typography variant="caption" color="textSecondary">{emp.email}</Typography>
                                                    </Box>
                                                </Box>
                                            </td>
                                            <td style={{ ...TD, maxWidth: '120px' }}>
                                                <Chip
                                                    label={emp.designation}
                                                    size="small"
                                                    sx={{
                                                        maxWidth: '100%',
                                                        height: '24px',
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        borderRadius: '8px',
                                                        bgcolor: '#eef4ff',
                                                        color: ACCENT,
                                                        border: 'none',
                                                        '& .MuiChip-label': {
                                                            display: 'block',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            padding: '0 8px'
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td style={TD}>
                                                {emp.date_of_joining ? formatDate(emp.date_of_joining) : 'N/A'}
                                            </td>
                                            <td style={{ ...TD, textAlign: 'center' }}>
                                                {emp.resignation_date ? (
                                                    <Chip
                                                        label={formatDate(emp.resignation_date)}
                                                        size="small"
                                                        sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: '#fef2f2', color: '#ef4444', border: 'none' }}
                                                    />
                                                ) : 'N/A'}
                                            </td>
                                            <td style={TD}>
                                                <Typography variant="body2" sx={reasonStyle} title={emp.resignation_reason}>
                                                    {emp.resignation_reason || 'N/A'}
                                                </Typography>
                                            </td>
                                            <td style={TD}>
                                                {emp.reporting_manager || 'N/A'}
                                            </td>
                                            <td style={TD}>
                                                ₹{emp.gross_pay ? (parseFloat(emp.gross_pay) * 12).toLocaleString() : 'N/A'}
                                            </td>
                                            <td style={TD}>
                                                <Tooltip title="Reactivate Employee">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleReactivate(emp.employee_no, e)}
                                                        disabled={isReactivating}
                                                        sx={REACTIVATE_BTN_SX}
                                                    >
                                                        <PersonAddIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </td>
                                        </Row>
                                    ))}
                            </tbody>
                        </table>
                    </TableWrap>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            ) : (
                <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 8 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PersonOffIcon />
                    </Box>
                    <Typography variant="h6" sx={{ color: '#111827', fontWeight: 700 }}>No resigned employees</Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        No resigned employees found matching your filters.
                    </Typography>
                </Paper>
            )}

            {/* Employee Details Modal/Section */}
            {selectedEmployee && (
                <div style={modalOverlayStyle} onClick={handleClose}>
                    <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                        {/* Close Button */}
                        <button style={closeButtonStyle} onClick={handleClose}>
                            <CloseIcon />
                        </button>

                        <div className="employee-container" style={detailsContainerStyle}>
                            {/* Left Section - Employee Details */}
                            <div className="employee-details" style={detailsSectionStyle}>
                                <h3 style={sectionTitleStyle}>Employee Details</h3>
                                <div style={detailsGridStyle}>
                                    <DetailItem label="Name" value={selectedEmployee.name} />
                                    <DetailItem label="Employee No" value={selectedEmployee.employee_no} />
                                    <DetailItem label="Designation" value={selectedEmployee.designation} />
                                    <DetailItem label="Department" value={selectedEmployee.department} />
                                    <DetailItem label="Division" value={selectedEmployee.division} />
                                    <DetailItem label="Location" value={selectedEmployee.location} />
                                    <DetailItem label="Email" value={selectedEmployee.email} />
                                    <DetailItem label="Mobile" value={selectedEmployee.mobile_number} />
                                    <DetailItem label="Reporting Manager" value={selectedEmployee.reporting_manager} />
                                    <DetailItem label="Date of Joining" value={formatDate(selectedEmployee.date_of_joining)} />
                                    <DetailItem label="Resignation Date" value={formatDate(selectedEmployee.resignation_date)} status />
                                    <DetailItem label="Resignation Reason" value={selectedEmployee.resignation_reason} />
                                    <DetailItem
                                        label="Annual CTC"
                                        value={selectedEmployee.gross_pay ? `₹${(parseFloat(selectedEmployee.gross_pay) * 12).toLocaleString()}` : 'N/A'}
                                    />
                                    <DetailItem label="Employment Status" value={selectedEmployee.employment_status} status />
                                </div>
                                <button
                                    style={reactivateButtonLargeStyle}
                                    onClick={() => handleReactivate(selectedEmployee.employee_no)}
                                    disabled={isReactivating}
                                >
                                    <PersonAddIcon style={{ fontSize: 20, marginRight: 8 }} />
                                    {isReactivating ? 'Reactivating...' : 'Reactivate Employee'}
                                </button>
                            </div>

                            {/* Right Section - Documents */}
                            <div className="employee-documents" style={documentsSectionStyle}>
                                <h3 style={sectionTitleStyle}>
                                    <DescriptionIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                    Documents
                                </h3>
                                <div style={documentsGridStyle}>
                                    {selectedEmployee.documents?.map((doc, index) => (
                                        <div key={index}>
                                            {doc.aadhar_card && (
                                                <DocumentLink
                                                    label="Aadhar Card"
                                                    url={`${BASE_API_URL}${doc.aadhar_card}`}
                                                />
                                            )}
                                            {doc.pan_card && (
                                                <DocumentLink
                                                    label="PAN Card"
                                                    url={`${BASE_API_URL}${doc.pan_card}`}
                                                />
                                            )}
                                            {doc.bank_proof && (
                                                <DocumentLink
                                                    label="Bank Proof"
                                                    url={`${BASE_API_URL}${doc.bank_proof}`}
                                                />
                                            )}
                                            {doc.education_certificate && (
                                                <DocumentLink
                                                    label="Educational Certificate"
                                                    url={`${BASE_API_URL}${doc.education_certificate}`}
                                                />
                                            )}
                                            {doc.experience_letter && (
                                                <DocumentLink
                                                    label="Experience Certificate"
                                                    url={`${BASE_API_URL}${doc.experience_letter}`}
                                                />
                                            )}
                                            {doc.nicsm_certificate && (
                                                <DocumentLink
                                                    label="Nicsm Certificate"
                                                    url={`${BASE_API_URL}${doc.nicsm_certificate}`}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    {selectedEmployee.resignation_info && (
                                        <div style={{ marginTop: '16px' }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 'bold' }}>
                                                Resignation Attachments
                                            </Typography>
                                            {selectedEmployee.resignation_info.file_1 && (
                                                <DocumentLink label="Resignation Attachment 1" url={`${BASE_API_URL}${selectedEmployee.resignation_info.file_1}`} />
                                            )}
                                            {selectedEmployee.resignation_info.file_2 && (
                                                <DocumentLink label="Resignation Attachment 2" url={`${BASE_API_URL}${selectedEmployee.resignation_info.file_2}`} />
                                            )}
                                            {selectedEmployee.resignation_info.file_3 && (
                                                <DocumentLink label="Resignation Attachment 3" url={`${BASE_API_URL}${selectedEmployee.resignation_info.file_3}`} />
                                            )}
                                            {selectedEmployee.resignation_info.file_4 && (
                                                <DocumentLink label="Resignation Attachment 4" url={`${BASE_API_URL}${selectedEmployee.resignation_info.file_4}`} />
                                            )}
                                            {!(selectedEmployee.resignation_info.file_1 || selectedEmployee.resignation_info.file_2 || selectedEmployee.resignation_info.file_3 || selectedEmployee.resignation_info.file_4) && (
                                                <Typography variant="body2" color="textSecondary">No resignation attachments.</Typography>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Box>
    );
};

// Helper Components
const DetailItem = ({ label, value, status }) => (
    <div style={detailItemStyle}>
        <span style={labelStyle}>{label}</span>
        <span style={status ? statusValueStyle : valueStyle}>{value || 'N/A'}</span>
    </div>
);

const DocumentLink = ({ label, url }) => (
    <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={documentLinkStyle}
    >
        <TabIcon style={{ fontSize: 18, marginRight: 8 }} />
        {label}
    </a>
);

// Styles
const reasonStyle = {
    maxWidth: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block'
};

const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(17,24,39,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1200,
    padding: '20px',
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '24px',
    maxWidth: '1100px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
    boxShadow: '0 24px 48px rgba(17,24,39,0.18)'
};

const closeButtonStyle = {
    position: 'absolute',
    top: '20px',
    right: '20px',
    background: '#f8fafc',
    color: '#6b7280',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    zIndex: 10,
    boxShadow: '0 2px 8px rgba(17,24,39,0.08)'
};

const detailsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: '1.5fr 1fr',
    gap: '32px',
    padding: '40px',
    paddingTop: '60px'
};

const detailsSectionStyle = {
    backgroundColor: '#ffffff',
    padding: '0',
    borderRadius: '0'
};

const documentsSectionStyle = {
    backgroundColor: '#f8fafc',
    padding: '24px',
    borderRadius: '16px',
    border: '1px solid #eef0f4'
};

const sectionTitleStyle = {
    fontSize: '20px',
    fontWeight: '800',
    color: '#111827',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #eef0f4',
    paddingBottom: '12px'
};

const detailsGridStyle = {
    display: 'grid',
    gap: '16px'
};

const detailItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    border: '1px solid #eef0f4'
};

const labelStyle = {
    fontWeight: '600',
    color: '#6b7280',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
};

const valueStyle = {
    color: '#111827',
    fontSize: '15px',
    fontWeight: '500'
};

const statusValueStyle = {
    ...valueStyle,
    backgroundColor: '#fef2f2',
    color: '#ef4444',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '700'
};

const documentsGridStyle = {
    display: 'grid',
    gap: '12px'
};

const documentLinkStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#fff',
    color: ACCENT,
    textDecoration: 'none',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    fontWeight: '500',
    border: '1px solid #eef4ff',
    boxShadow: '0 2px 4px rgba(17,24,39,0.03)'
};

const reactivateButtonLargeStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '16px',
    marginTop: '32px',
    backgroundColor: '#16a34a',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '700',
    transition: 'all 0.2s ease',
    boxShadow: '0 6px 16px rgba(22, 163, 74, 0.22)',
};

export default ResignedEmp;
